import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      light: '#4dabf5',
      main: '#1976d2',
      dark: '#1565c0',
      contrastText: '#fff',
    },
    secondary: {
      light: '#ff4081',
      main: '#dc004e',
      dark: '#c51162',
      contrastText: '#fff',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#546e7a',
    },
    error: {
      main: '#f44336',
      light: '#ffcdd2',
      dark: '#d32f2f',
    },
    warning: {
      main: '#ff9800',
      light: '#ffecb3',
      dark: '#f57c00',
    },
    success: {
      main: '#4caf50',
      light: '#c8e6c9',
      dark: '#388e3c',
    },
    info: {
      main: '#2196f3',
      light: '#bbdefb',
      dark: '#1976d2',
    },
  },
  // Customize breakpoints for better mobile responsiveness
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      letterSpacing: '-0.01562em',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      letterSpacing: '-0.00833em',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      letterSpacing: '0em',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      letterSpacing: '0.00735em',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      letterSpacing: '0em',
      '@media (max-width:600px)': {
        fontSize: '1.1rem',
      },
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      letterSpacing: '0.0075em',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.00714em',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      letterSpacing: '0.01071em',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.02857em',
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      letterSpacing: '0.03333em',
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 400,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 2,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f8f9fa',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a1a1a1',
          },
          // Add meta viewport for mobile responsiveness
          '@media (max-width:600px)': {
            WebkitOverflowScrolling: 'touch',
          },
        },
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          boxSizing: 'border-box',
        }
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 4,
          padding: '8px 16px',
          transition: 'all 0.2s',
          '@media (max-width:600px)': {
            minHeight: '42px',
          },
        },
        contained: {
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
        sizeMedium: {
          '@media (max-width:600px)': {
            fontSize: '0.8125rem',
            padding: '6px 12px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 4, // Subtle rounding for all papers
          transition: 'box-shadow 0.3s',
        },
        elevation1: {
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        },
        elevation2: {
          boxShadow: '0 3px 15px rgba(0,0,0,0.08)',
        },
        // Enhanced shadow for status cards specifically - more crisp definition without too much height
        elevation3: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.32), 0 0 0 1px rgba(0,0,0,0.05)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '16px',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          '@media (max-width:600px)': {
            padding: '12px 8px',
          },
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#f8f9fa',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.02)',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          transition: 'all 0.2s',
          minHeight: '56px',
          '&.Mui-selected': {
            fontWeight: 600,
          },
          '@media (max-width:600px)': {
            minHeight: '48px',
            minWidth: '100px !important',
            fontSize: '0.75rem',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
        sizeSmall: {
          height: 24,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          alignItems: 'center',
        },
        standardSuccess: {
          backgroundColor: 'rgba(76, 175, 80, 0.12)',
          borderLeft: '4px solid #429e45',
        },
        standardInfo: {
          backgroundColor: 'rgba(33, 150, 243, 0.12)',
          borderLeft: '4px solid #2196f3',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 152, 0, 0.12)',
          borderLeft: '4px solid #ffa319',
        },
        standardError: {
          backgroundColor: 'rgba(244, 67, 54, 0.12)',
          borderLeft: '4px solid #eb3e31',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiTypography-h1, &.MuiTypography-h2, &.MuiTypography-h3, &.MuiTypography-h4, &.MuiTypography-h5, &.MuiTypography-h6': {
            color: '#2c3e50',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s',
          '@media (max-width:600px)': {
            padding: '8px',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          margin: '16px 0',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          overflow: 'hidden',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '16px 12px',
          },
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
          '@media (max-width:600px)': {
            padding: '8px 12px',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            paddingLeft: 12,
            paddingRight: 12,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          width: 'auto',
          minWidth: 256,
        },
      },
    },
  },
  shadows: [
    'none',
    '0 2px 5px rgba(0,0,0,0.05)',
    '0 3px 8px rgba(0,0,0,0.08)',
    // Enhanced shadow for status cards - more definition without excessive height
    '0 4px 12px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)',
    '0 5px 15px rgba(0,0,0,0.12)',
    '0 6px 18px rgba(0,0,0,0.14)',
    '0 7px 21px rgba(0,0,0,0.16)',
    '0 8px 24px rgba(0,0,0,0.18)',
    '0 9px 27px rgba(0,0,0,0.2)',
    '0 10px 30px rgba(0,0,0,0.22)',
    '0 11px 33px rgba(0,0,0,0.24)',
    '0 12px 36px rgba(0,0,0,0.26)',
    '0 13px 39px rgba(0,0,0,0.28)',
    '0 14px 42px rgba(0,0,0,0.3)',
    '0 15px 45px rgba(0,0,0,0.32)',
    '0 16px 48px rgba(0,0,0,0.34)',
    '0 17px 51px rgba(0,0,0,0.36)',
    '0 18px 54px rgba(0,0,0,0.38)',
    '0 19px 57px rgba(0,0,0,0.4)',
    '0 20px 60px rgba(0,0,0,0.42)',
    '0 21px 63px rgba(0,0,0,0.44)',
    '0 22px 66px rgba(0,0,0,0.46)',
    '0 23px 69px rgba(0,0,0,0.48)',
    '0 24px 72px rgba(0,0,0,0.5)',
  ],
});

export default theme;